/* ==========================================================================
   Pila — Geo detection
   Shared by the geo banner and the international page, so both agree on
   where a visitor is and there is one place to fix when they don't.

   Priority: a country code in the URL, then /api/geo (which echoes Vercel's
   edge resolution), then the region subtag of the browser locale.

   Every answer is a hint. Nothing here gates content, and both callers let
   the visitor override the result, because a VPN, a trip, or a corporate
   egress point will get this wrong.
   ========================================================================== */

(function () {
  'use strict';

  var CACHE_KEY = 'pila:geo:country';

  function isCode(value) {
    return typeof value === 'string' && /^[A-Za-z]{2}$/.test(value);
  }

  // ?country=DE, or ?c=DE
  function fromParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      var code = (params.get('country') || params.get('c') || '').trim();
      return isCode(code) ? code.toUpperCase() : null;
    } catch (e) {
      return null;
    }
  }

  function fromLocale() {
    var langs = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || ''];
    for (var i = 0; i < langs.length; i++) {
      var m = /[-_]([A-Za-z]{2})$/.exec(langs[i]);
      if (m) return m[1].toUpperCase();
    }
    return null;
  }

  // Session-scoped so a visitor moving between pages asks the edge once.
  // Deliberately not localStorage: someone who opens a laptop in another
  // country should get a fresh answer, not last month's.
  function cached() {
    try {
      var v = window.sessionStorage.getItem(CACHE_KEY);
      return isCode(v) ? v : null;
    } catch (e) {
      return null;
    }
  }

  function cache(code) {
    try {
      if (isCode(code)) window.sessionStorage.setItem(CACHE_KEY, code);
    } catch (e) { /* private mode, storage full: not worth failing over */ }
    return code;
  }

  var inFlight = null;

  function fromApi(apiBase) {
    if (!apiBase) return Promise.resolve(null);
    if (inFlight) return inFlight;

    inFlight = fetch(apiBase.replace(/\/$/, '') + '/api/geo', { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return (d && isCode(d.country)) ? d.country.toUpperCase() : null; })
      .catch(function () { return null; });

    return inFlight;
  }

  /**
   * Resolve the visitor's country.
   * @param {string} apiBase   Origin of the Pila API. Falsy skips the call.
   * @param {string[]} [known] Optional allowlist. A code outside it is
   *                           treated as unknown, so a junk ?country= value
   *                           falls through to detection instead of sticking.
   * @returns {Promise<string|null>} ISO 3166-1 alpha-2, or null.
   */
  function detect(apiBase, known) {
    function ok(code) {
      if (!code) return null;
      if (known && known.indexOf(code) === -1) return null;
      return code;
    }

    var stated = ok(fromParam());
    if (stated) return Promise.resolve(stated);

    var seen = ok(cached());
    if (seen) return Promise.resolve(seen);

    return fromApi(apiBase).then(function (code) {
      return ok(code) ? cache(code) : ok(fromLocale());
    });
  }

  window.PilaGeo = {
    detect: detect,
    fromParam: fromParam,
    fromLocale: fromLocale
  };
})();
